import React, { useState, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  useStripe,
  useElements,
  PaymentElement,
} from '@stripe/react-stripe-js';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import makeStyles from '@material-ui/core/styles/makeStyles';

const GREEN = '#4EA14C';

const STRIPE_TEXTS = {
  'lt-LT': {
    paymentFailed: 'Mokėjimas nepavyko.',
    processing: 'Apdorojama…',
    pay: 'Mokėti',
    cancel: 'Atšaukti',
    missingConfig: 'Trūksta mokėjimo nustatymų. Bandykite dar kartą.',
  },
  'en-EU': {
    paymentFailed: 'Payment failed.',
    processing: 'Processing…',
    pay: 'Pay',
    cancel: 'Cancel',
    missingConfig: 'Missing payment configuration. Please try again.',
  },
};

const useStyles = makeStyles((theme) => ({
  form: {
    marginTop: theme.spacing(2),
  },
  error: {
    color: '#f44336',
    marginTop: theme.spacing(1),
  },
  actions: {
    marginTop: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
  },
  payButton: {
    backgroundColor: GREEN,
    color: '#fff',
    borderRadius: 28,
    '&:hover': {
      backgroundColor: GREEN,
      opacity: 0.9,
    },
  },
  cancelButton: {
    borderRadius: 28,
    borderColor: 'rgba(255, 255, 255, 0.5)',
    color: 'rgba(255, 255, 255, 0.9)',
    '&:hover': {
      borderColor: 'rgba(255, 255, 255, 0.8)',
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  },
}));

function PaymentFormInner({
  buyPath,
  returnUrlSearch,
  locale,
  onSuccess,
  onCancel,
}) {
  const classes = useStyles();
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState(null);
  const [processing, setProcessing] = useState(false);
  const t = STRIPE_TEXTS[locale] || STRIPE_TEXTS['en-EU'];

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setErrorMessage(null);

    const path = buyPath || '/lt-LT/buy';
    const returnUrl = `${window.location.origin}${path}${returnUrlSearch}${returnUrlSearch ? '&' : '?'}payment=complete`;

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: returnUrl,
      },
      redirect: 'if_required',
    });

    if (error) {
      setErrorMessage(error.message || t.paymentFailed);
      setProcessing(false);
      return;
    }

    setProcessing(false);
    onSuccess();
  };

  return (
    <form onSubmit={handleSubmit} className={classes.form}>
      <PaymentElement />
      {errorMessage && (
        <Typography className={classes.error} variant="body2">
          {errorMessage}
        </Typography>
      )}
      <Box className={classes.actions}>
        <Button
          type="submit"
          variant="contained"
          className={classes.payButton}
          disabled={!stripe || processing}
        >
          {processing ? t.processing : t.pay}
        </Button>
        <Button
          type="button"
          variant="outlined"
          className={classes.cancelButton}
          onClick={onCancel}
          disabled={processing}
        >
          {t.cancel}
        </Button>
      </Box>
    </form>
  );
}

const StripePaymentForm = ({
  clientSecret,
  publishableKey,
  buyPath = '/lt-LT/buy',
  locale = 'lt-LT',
  returnUrlSearch = '',
  onSuccess,
  onCancel,
}) => {
  const t = STRIPE_TEXTS[locale] || STRIPE_TEXTS['en-EU'];
  const stripePromise = useMemo(() => {
    const key = publishableKey || process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    return key ? loadStripe(key) : null;
  }, [publishableKey]);

  const options = useMemo(
    () => ({
      clientSecret,
      appearance: {
        theme: 'night',
        variables: {
          colorPrimary: GREEN,
          colorBackground: '#1a1a1a',
          colorText: '#ffffff',
          colorTextSecondary: '#b0b0b0',
          colorTextPlaceholder: '#808080',
          colorDanger: '#f44336',
          borderRadius: '12px',
        },
      },
    }),
    [clientSecret],
  );

  if (!clientSecret || !stripePromise) {
    return (
      <Typography style={{ marginTop: 16, color: 'rgba(255, 255, 255, 0.7)' }}>
        {t.missingConfig}
      </Typography>
    );
  }

  return (
    <Elements stripe={stripePromise} options={options}>
      <PaymentFormInner
        buyPath={buyPath}
        returnUrlSearch={returnUrlSearch}
        locale={locale}
        onSuccess={onSuccess}
        onCancel={onCancel}
      />
    </Elements>
  );
};

export default StripePaymentForm;
